import type { FormDialogRef } from '@/components'
import {
  AbsenceTypeCombobox,
  Checkbox,
  FAIcon,
  Field,
  FieldGroup,
  FieldLabel,
  FormDialog,
  Label,
} from '@/components'
import { MIGRATE_ABSENCE_TYPE_MUTATION } from '@/modules/timesheets/mutations'
import {
  ABSENCE_TYPE_USAGE_QUERY,
  ABSENCE_TYPES_COMBOBOX_QUERY,
} from '@/modules/timesheets/queries'
import type { AbsenceTypeNode } from '@/modules/timesheets/types.graphql'
import { useFormWatch } from '@/utils/hooks/form-hooks'
import { useOnErrorAuto } from '@/utils/hooks'
import { useMutation, useQuery } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ReactNode } from 'react'
import { useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { WholeDayRoundingNotice } from './whole-day-rounding-notice'

const MigrateAbsenceTypeSchema = z.object({
  targetId: z.string().min(1),
  deleteSource: z.boolean(),
})

type MigrateAbsenceTypeForm = z.infer<typeof MigrateAbsenceTypeSchema>

interface Props {
  absenceType: AbsenceTypeNode
  /** Override the default trigger element (e.g. for the mobile cards view) */
  trigger?: ReactNode
}

export function MigrateAbsenceTypeDialog({ absenceType, trigger }: Props) {
  const translations = useTranslate({
    buttonLabel: 'settings.absence.migrate.button-label',
    header: 'settings.absence.migrate.header',
    description: ['settings.absence.migrate.description', { name: '' }],
    target: 'settings.absence.migrate.target',
    summary: [
      'settings.absence.migrate.summary',
      { absences: <strong />, users: <strong />, adjustments: <strong /> },
    ],
    deleteSource: ['settings.absence.migrate.delete-source', { name: '' }],
    confirm: 'settings.absence.migrate.confirm',
    success: ['settings.absence.migrate.success', { count: 0, name: '' }],
    error: 'settings.absence.migrate.error',
  })

  const dialogRef = useRef<FormDialogRef>(null)
  const [open, setOpen] = useState(false)

  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const { data: usageData } = useQuery(ABSENCE_TYPE_USAGE_QUERY, {
    variables: { id: absenceType.id },
    skip: !open,
    fetchPolicy: 'network-only',
  })
  const usage = usageData?.absenceType?.usage
  const rounding = usageData?.absenceType?.wholeDayRounding

  const { data: absenceTypesData } = useQuery(ABSENCE_TYPES_COMBOBOX_QUERY, {
    skip: !open,
  })

  const [migrate, { loading }] = useMutation(MIGRATE_ABSENCE_TYPE_MUTATION, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onCompleted({ migrateAbsenceType }) {
      addToast(
        'success',
        translations.success({
          count: migrateAbsenceType.migratedAbsenceCount,
          name: migrateAbsenceType.target.name,
        })
      )
    },
    onError: onErrorAuto(translations.error),
  })

  const form = useForm<MigrateAbsenceTypeForm>({
    resolver: zodResolver(MigrateAbsenceTypeSchema),
    defaultValues: {
      targetId: '',
      deleteSource: true,
    },
  })

  const targetId = useFormWatch(form, 'targetId')
  const target = absenceTypesData?.allAbsenceTypes.find(
    type => type.id === targetId
  )

  function resetForm() {
    setTimeout(() => form.reset(), 300)
  }

  async function handleSubmit(values: MigrateAbsenceTypeForm) {
    const { errors } = await migrate({
      variables: {
        sourceId: absenceType.id,
        targetId: values.targetId,
        deleteSource: values.deleteSource,
      },
    })
    if (!errors) dialogRef.current?.close()
  }

  return (
    <FormDialog
      ref={dialogRef}
      className="w-[480px]"
      form={form}
      header={translations.header}
      description={translations.description({ name: absenceType.name })}
      trigger={
        trigger ?? (
          <div role="button" aria-label={translations.buttonLabel}>
            <FAIcon
              className="text-gray-400 hover:text-primary"
              icon="right-left"
              cursor="pointer"
            />
          </div>
        )
      }
      confirmText={translations.confirm}
      confirmDisabled={!targetId || loading}
      onOpenChange={setOpen}
      onClose={resetForm}
      onSubmit={handleSubmit}
    >
      <FieldGroup>
        <Controller
          control={form.control}
          name="targetId"
          render={({ field }) => (
            <Field>
              <FieldLabel>{translations.target}</FieldLabel>

              <AbsenceTypeCombobox
                mode="single"
                value={field.value}
                hideItem={type => type.id === absenceType.id}
                onChange={field.onChange}
              />
            </Field>
          )}
        />

        {!!usage && (
          <p className="text-sm text-pretty">
            {translations.summary({
              absences: <strong>{usage.absenceCount}</strong>,
              users: <strong>{usage.userCount}</strong>,
              adjustments: <strong>{usage.manualTransactionCount}</strong>,
            })}
          </p>
        )}

        {target?.durationMode === 'DAILY' && !!rounding && (
          <WholeDayRoundingNotice rounding={rounding} />
        )}

        <Controller
          control={form.control}
          name="deleteSource"
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Checkbox
                id="migrate-delete-source"
                value={field.value}
                size="sm"
                onChange={checked => field.onChange(checked === true)}
              />

              <Label className="text-sm" htmlFor="migrate-delete-source">
                {translations.deleteSource({ name: absenceType.name })}
              </Label>
            </div>
          )}
        />
      </FieldGroup>
    </FormDialog>
  )
}
